import { Link, type LinkProps } from "@tanstack/react-router"
import { EntityMark } from "@/components/entity-mark"
import { HiringRegionChips } from "@/components/hiring-region-picker"
import { TagChips } from "@/components/chips"
import { cn } from "@/lib/utils"

/**
 * The one list row for jobs, hackathons and other domain entities: optional
 * company mark, title + chip cluster, meta line, region/skill chips, hover cue.
 * Built on the shared `mkt-page-row` CSS vocabulary.
 */
export function EntityRow({
  to,
  params,
  search,
  onClick,
  mark,
  title,
  chips,
  meta,
  regions,
  tags,
  cue,
  action,
  className,
}: {
  to?: LinkProps["to"]
  params?: LinkProps["params"]
  search?: LinkProps["search"]
  onClick?: () => void
  /** Company/host tile: pass {name, imageUrl} for the standard EntityMark, or a custom node. */
  mark?: { name: string; imageUrl?: string | null } | React.ReactElement
  title: React.ReactNode
  chips?: React.ReactNode
  meta?: React.ReactNode
  regions?: string[]
  tags?: { items: string[]; max?: number }
  cue?: string
  /** Rendered outside the row link (e.g. a Pay button) via mkt-page-row--with-action. */
  action?: React.ReactNode
  className?: string
}) {
  const body = (
    <>
      <div className="mkt-page-row__top">
        <h2 className="mkt-page-row-title">{title}</h2>
        {chips && <div className="mkt-page-row__chips">{chips}</div>}
      </div>
      {meta && <p className="mkt-page-meta">{meta}</p>}
      {regions && regions.length > 0 && (
        <HiringRegionChips regions={regions} className="mt-2" />
      )}
      {tags && tags.items.length > 0 && (
        <div className="mkt-page-row__skills">
          <TagChips items={tags.items} max={tags.max} />
        </div>
      )}
      {cue && <span className="mkt-page-row__cue">{cue}</span>}
    </>
  )
  const inner = mark ? (
    <div className="flex items-start gap-3.5">
      {"name" in mark ? (
        <EntityMark name={mark.name} imageUrl={mark.imageUrl} />
      ) : (
        mark
      )}
      <div className="min-w-0 flex-1">{body}</div>
    </div>
  ) : (
    body
  )

  if (to && action) {
    return (
      <div
        className={cn("mkt-page-row mkt-page-row--with-action", className)}
      >
        <Link
          to={to}
          params={params}
          search={search}
          className="mkt-page-row__main"
        >
          {inner}
        </Link>
        {action}
      </div>
    )
  }
  if (to) {
    return (
      <Link
        to={to}
        params={params}
        search={search}
        className={cn("mkt-page-row", className)}
      >
        {inner}
      </Link>
    )
  }
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn("mkt-page-row w-full text-left", className)}
      >
        {inner}
      </button>
    )
  }
  return <div className={cn("mkt-page-row", className)}>{inner}</div>
}
